import { useState, useMemo } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkle, LockKey, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, saveAuth } from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);

  if (!token) {
    return (
      <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center px-6 text-center" data-testid="reset-no-token">
        <div className="max-w-md">
          <div className="font-serif-display text-3xl text-[#1C1B1A]">Reset link is missing.</div>
          <p className="text-[#6B6A68] mt-2 text-sm">Open the link from the email we sent — it includes a one-time token.</p>
          <Link to="/forgot-password" className="mt-6 inline-block text-sm px-5 py-2.5 rounded-md bg-[#1C1B1A] text-[#FDFCF8]">Request a new link</Link>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return toast.error("Password must be at least 8 characters.");
    if (pw !== pw2) return toast.error("Passwords don't match.");
    setLoading(true);
    try {
      const r = await api.post("/auth/reset-password", { token, new_password: pw });
      saveAuth(r.data.token, r.data.user);
      toast.success("Password updated. You're signed in.");
      nav("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed — request a new link.");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#FDFCF8] flex flex-col" data-testid="reset-password-page">
      <nav className="border-b hairline">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#1C1B1A] text-[#FDFCF8] flex items-center justify-center"><Sparkle size={18} weight="duotone" /></div>
            <span className="font-serif-display text-xl">My Silo</span>
          </Link>
          <Link to="/login" className="text-sm text-[#6B6A68] hover:text-[#1C1B1A] flex items-center gap-1">
            <ArrowLeft size={14} /> Back to sign in
          </Link>
        </div>
      </nav>
      <main className="siloart-copy flex-1 flex items-center justify-center px-6 py-12">
        <motion.form initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} onSubmit={submit}
          className="w-full max-w-md bg-white border hairline rounded-xl shadow-sm p-8">
          <h1 className="font-serif-display text-3xl text-[#1C1B1A]">Set a new password.</h1>
          <p className="text-[#6B6A68] text-sm mt-1">At least 8 characters. Pick something you'll remember this time.</p>

          <label className="block text-sm text-[#6B6A68] mt-6 mb-1.5">New password</label>
          <input data-testid="reset-pw" type="password" required minLength={8} value={pw} onChange={(e) => setPw(e.target.value)}
            className="w-full bg-white border hairline rounded-md px-4 py-3 focus:outline-none focus:ring-1 focus:ring-[#4A5D4E] focus:border-[#4A5D4E]" placeholder="••••••••" />
          <label className="block text-sm text-[#6B6A68] mt-4 mb-1.5">Confirm new password</label>
          <input data-testid="reset-pw2" type="password" required minLength={8} value={pw2} onChange={(e) => setPw2(e.target.value)}
            className="w-full bg-white border hairline rounded-md px-4 py-3 focus:outline-none focus:ring-1 focus:ring-[#4A5D4E] focus:border-[#4A5D4E]" placeholder="••••••••" />
          <button type="submit" disabled={loading} data-testid="reset-submit"
            className="mt-6 w-full bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] px-6 py-3 rounded-md transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
            <LockKey size={16} weight="duotone" /> {loading ? "Saving…" : "Reset password"}
          </button>
        </motion.form>
      </main>
    </div>
  );
}
